import { useReactiveVar } from "@apollo/client";
import { currentConfigVar } from "apollo/cache/config";
import { Owner } from "components/core";
import lodash from "lodash";
import moment from "moment";
import { FC } from "react";
import {classNames} from "utils";


export const headerMap = {
  sn: "SN",
  stsNumber: "STS Number",
  meterNumber: "Meter Number",
};

export const columns = Object.keys(headerMap);


interface FactoryAcceptanceTestServiceOrderDetailsProps {
  serviceOrder: {
    code: string;
    status: string;
    priority: string;
    meterContractor: {
      code: string;
      name: string;
      contactPerson?: {
        fullName: string,
        emailAddress: string,
        phoneNumber: string,
      }
      testingFacility?: {
        name: string,
        address: string,
        ghanaPostAddress: string,
        contactPerson: {
          fullName: string,
          emailAddress: string,
          phoneNumber: string,
        }
      }
    };
    testSet?: {
      meterBrand: any
      meterModel: any
      meterPhase: "1 Phase" | "3 Phase"
      quantity: number
      content: {
        stsNumber: string
        meterNumber: string
      }[]
    }
    meterDeliveryRequest: {
      code: string;
      request: {
        testAtOwnFacility: boolean;
      }
    };
    assigner: {
      _id: string;
      code: string;
      lastName: string;
      firstName: string;
      profileImageUrl: string;
      phoneNumber: string;
      emailAddress: string;
    };
    assignee: {
      _id: string;
      code: string;
      lastName: string;
      firstName: string;
      profileImageUrl: string;
      phoneNumber: string;
      emailAddress: string;
    };
    approver: {
      _id: string;
      code: string;
      lastName: string;
      firstName: string;
      profileImageUrl: string;
      phoneNumber: string;
      emailAddress: string;
    };
    disapprover: {
      _id: string;
      code: string;
      lastName: string;
      firstName: string;
      profileImageUrl: string;
      phoneNumber: string;
      emailAddress: string;
    };
    estimatedResolutionDate: string;
    createdAt: string;
    updatedAt: string;
  };
}

export const FactoryAcceptanceTestServiceOrderDetails: FC<
  FactoryAcceptanceTestServiceOrderDetailsProps
> = ({ serviceOrder }) => {
  const { dateFormat } = useReactiveVar(currentConfigVar);

  return (
    <div className="space-y-6 divide-y divide-gray-200">
      <div>
        <span className="text-xs font-light">Service Order Information</span>
        <div className="grid grid-cols-3 gap-6 mt-2">
          <div>
            <span className="block text-sm font-light text-gray-700">Code</span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.code || "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">Date</span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.createdAt
                ? moment(serviceOrder.createdAt).format(dateFormat)
                : "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              Status
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {lodash.startCase(serviceOrder?.status) || "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              Priority
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {lodash.startCase(serviceOrder?.priority) || "N/A"}
            </div>
          </div>
          <div className="col-span-2">
            <span className="block text-sm font-light text-gray-700">
              Estimated Resolution Date
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.estimatedResolutionDate
                ? moment(serviceOrder.estimatedResolutionDate).format(
                    dateFormat,
                  )
                : "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              Meter Delivery Request Code
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.meterDeliveryRequest?.code ||"N/A" }
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              Test At Own Facility
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {Boolean(
                serviceOrder?.meterDeliveryRequest?.request?.testAtOwnFacility)
                ? "Yes"
                : "No"
              }
            </div>
          </div>
        </div>
      </div>
      <div className="pt-6">
        <span className="text-xs font-light">Meter Contractor Information</span>
        <div className="grid grid-cols-3 gap-6 mt-2">
          <div className="col-span-2">
            <span className="block text-sm font-light text-gray-700">
              Name
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.meterContractor?.name || "N/A"}
            </div>
          </div>

          <div>
            <span className="block text-sm font-light text-gray-700">
              Code
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.meterContractor?.code || "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              Contact Person
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.meterContractor?.contactPerson?.fullName || "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              Phone Number
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.meterContractor?.contactPerson?.phoneNumber || "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              Email Address
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.meterContractor?.contactPerson?.emailAddress || "N/A"}
            </div>
          </div>
        </div>

      </div>

      {
        Boolean(
          serviceOrder?.meterDeliveryRequest?.request?.testAtOwnFacility
        ) && serviceOrder?.meterContractor?.testingFacility?.name &&  (
          <div className={"pt-6"}>
            <span className="text-xs font-light">Testing Facility Information</span>
            <div className="grid grid-cols-3 gap-6 mt-2">
              <div className="col-span-3">
                <span className="block text-sm font-light text-gray-700">
                  Name
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  {serviceOrder?.meterContractor?.testingFacility?.name || "N/A"}
                </div>
              </div>

              <div className=" col-start-1 col-span-2">
                <span className="block text-sm font-light text-gray-700">
                  Residential Address
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  {serviceOrder?.meterContractor?.testingFacility?.address || "N/A"}
                </div>
              </div>

              <div>
                <span className="block text-sm font-light text-gray-700">
                  GhanaPost Address
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  {serviceOrder?.meterContractor?.testingFacility?.ghanaPostAddress || "N/A"}
                </div>
              </div>
              <div>
                <span className="block text-sm font-light text-gray-700">
                  Contact Person
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  {serviceOrder?.meterContractor?.testingFacility?.contactPerson?.fullName || "N/A"}
                </div>
              </div>
              <div>
                <span className="block text-sm font-light text-gray-700">
                  Phone Number
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  {serviceOrder?.meterContractor?.testingFacility?.contactPerson?.phoneNumber || "N/A"}
                </div>
              </div>
              <div>
                <span className="block text-sm font-light text-gray-700">
                  Email Address
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  {serviceOrder?.meterContractor?.testingFacility?.contactPerson?.emailAddress || "N/A"}
                </div>
              </div>
            </div>
          </div>
        )
      }
      <div className='pt-6' >
        <span className="text-xs font-light">Test Set Information</span>
        <div className='grid grid-cols-4 gap-6 mt-2'>
          <div className="col-span-1">
            <span className="block text-sm font-light text-gray-700">
              Quantity
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.testSet?.content?.length || "N/A"} Meters
            </div>
          </div>
          <div className="col-span-1">
            <span className="block text-sm font-light text-gray-700">
              Meter Phase
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.testSet?.meterPhase || "N/A"}
            </div>
          </div>
          <div className="col-span-1">
            <span className="block text-sm font-light text-gray-700">
              Meter Brand
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.testSet?.meterBrand?.name || "N/A"}
            </div>
          </div>
          <div className="col-span-1">
            <span className="block text-sm font-light text-gray-700">
              Meter Model
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.testSet?.meterModel?.name || "N/A"}
            </div>
          </div>
          <div className={"max-h-[300px] overflow-y-auto col-span-4"}>
            <table className=" min-w-full  divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10 border-b">
              <tr>
                {columns?.map((column: any, idx: number) => (
                  <th
                    scope="col"
                    key={idx}
                    className={classNames(
                      idx === 0 ? "sticky left-0 bg-gray-50" : "",
                      "px-6 py-3 mt-0 text-left text-xs font-medium whitespace-nowrap text-gray-500 uppercase tracking-wider"
                    )}
                  >
                    {lodash.get(headerMap, column)}
                  </th>
                ))}
              </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
              {serviceOrder?.testSet?.content?.map((item, key) => {
                return (
                  <tr key={key}>
                    {columns?.map((column, idx) => {
                      return (
                        <td
                          key={idx}
                          className={classNames(
                            idx === 0 ? "sticky left-0 bg-gray-50" : "",
                            "px-6 py-4 whitespace-nowrap text-sm ",
                            "text-gray-500"
                          )}
                        >
                          {column === "sn"   
                            ? (key + 1)
                            : lodash.get(item, column, "N/A")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {serviceOrder?.status !== "Pending" && (
        <div className="pt-6">
          <span className="text-xs font-light">Actors Information</span>
          <div className="grid grid-cols-2 gap-6 mt-2">
            <div>
              <span className="block text-sm font-light text-gray-700">
                Assigner
              </span>
              <div className="mt-1 block w-full sm:text-sm">
                <Owner owner={serviceOrder?.assigner} type="staff-accounts" />
              </div>
            </div>
            <div>
              <span className="block text-sm font-light text-gray-700">
                Assignee
              </span>
              <div className="mt-1 block w-full sm:text-sm">
                <Owner owner={serviceOrder?.assignee} type="field-agents" />
              </div>
            </div>{" "}
          </div>
          <div className="grid grid-cols-3 gap-6 mt-2">
            {serviceOrder?.status === "Disapproved" && (
              <div>
                <span className="block text-sm font-light text-gray-700">
                  Disapprover
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  <Owner
                    owner={serviceOrder?.disapprover}
                    type="staff-accounts"
                  />
                </div>
              </div>
            )}
            {serviceOrder?.status === "Completed" && (
              <div>
                <span className="block text-sm font-light text-gray-700">
                  Approver
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  <Owner owner={serviceOrder?.approver} type="staff-accounts" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FactoryAcceptanceTestServiceOrderDetails;
